/*
 * An order can need more than one warehouse: what one place has in stock the
 * next one does not. Each warehouse it takes is one shipment, and a shipment is
 * bought on its own — the API takes a single warehouse per request.
 */

export type ShipmentStatus = 'draft' | 'busy' | 'shipped' | 'failed';

export type ShipmentPlan = {
  id: string;
  providerId: string;
  itemIds: string[];
  status: ShipmentStatus;
  error?: string;
};

/** A warehouse reduced to the question the planner asks: what can it ship? */
export type PlannerProvider = {
  id: string;
  /** The lines it holds stock for, so the ones it can ship today. */
  availableItemIds: string[];
  isDefault?: boolean;
};

export type PlanInput = {
  providers: PlannerProvider[];
  /** Every line still owed on the order. */
  pendingItemIds: string[];
  /** Where the order would ship from if one warehouse could do it all. */
  preferredProviderId?: string;
};

let sequence = 0;

function nextId() {
  sequence += 1;
  return `shipment-${sequence}`;
}

export function createPlan(
  providerId: string,
  itemIds: string[],
): ShipmentPlan {
  return { id: nextId(), providerId, itemIds, status: 'draft' };
}

/** Still to be bought, so still open to being rearranged. */
export function isOpen(plan: { status: ShipmentStatus }) {
  return plan.status === 'draft' || plan.status === 'failed';
}

function coverage(provider: PlannerProvider, itemIds: string[]) {
  return itemIds.filter(itemId => provider.availableItemIds.includes(itemId));
}

export function hasAvailableShipmentItems(
  plan: ShipmentPlan | undefined,
  providers: PlannerProvider[],
) {
  if (!plan) {
    return false;
  }

  const provider = providers.find(item => item.id === plan.providerId);
  return !!provider && coverage(provider, plan.itemIds).length > 0;
}

/**
 * The fewest warehouses that cover the order, preferring the one the order
 * would have shipped from anyway. Greedy rather than exhaustive: an order has a
 * handful of lines, and a plan that is easy to explain beats an optimal one.
 */
export function planShipments({
  providers,
  pendingItemIds,
  preferredProviderId,
}: PlanInput): ShipmentPlan[] {
  let remaining = [...pendingItemIds];
  const plans: ShipmentPlan[] = [];
  const used: string[] = [];

  const preferred = providers.find(
    provider => provider.id === preferredProviderId,
  );
  const ordered = [
    ...(preferred ? [preferred] : []),
    ...providers.filter(provider => provider.id !== preferredProviderId),
  ];

  while (remaining.length > 0) {
    let best: { provider: PlannerProvider; items: string[] } | undefined;

    ordered.forEach(provider => {
      if (used.includes(provider.id)) {
        return;
      }
      const items = coverage(provider, remaining);
      if (items.length === 0) {
        return;
      }
      /* Strictly better only: a tie keeps the earlier, better-ranked warehouse. */
      if (!best || items.length > best.items.length) {
        best = { provider, items };
      }
    });

    if (!best) {
      break;
    }

    plans.push(createPlan(best.provider.id, best.items));
    used.push(best.provider.id);
    remaining = remaining.filter(itemId => !best?.items.includes(itemId));
  }

  return plans;
}

/**
 * The plan after the order changed under it. A fulfilled line leaves its
 * shipment, a line nobody has claimed gets planned, and everything the user
 * arranged by hand stays where they put it. A line no warehouse stocks stays
 * unclaimed, since no shipment can carry it.
 *
 * A shipment that has gone out is kept only until the order stops owing its
 * lines: from then on the label it produced is what the order has to show, and
 * a card holding the same lines would say it twice.
 */
export function reconcilePlans(
  existing: ShipmentPlan[],
  input: PlanInput,
): ShipmentPlan[] {
  const { pendingItemIds } = input;

  const kept = existing
    .map(plan => ({
      ...plan,
      itemIds: plan.itemIds.filter(itemId => pendingItemIds.includes(itemId)),
    }))
    .filter(plan => plan.itemIds.length > 0);

  const claimed = kept.flatMap(plan => plan.itemIds);
  const unclaimed = pendingItemIds.filter(itemId => !claimed.includes(itemId));

  if (unclaimed.length === 0) {
    return kept;
  }

  return [...kept, ...planShipments({ ...input, pendingItemIds: unclaimed })];
}

/** Empties are dropped: a shipment with nothing in it is not a shipment. */
function withoutEmpty(plans: ShipmentPlan[]) {
  return plans.filter(plan => plan.itemIds.length > 0);
}

function removeItem(plans: ShipmentPlan[], itemId: string) {
  return withoutEmpty(
    plans.map(plan =>
      isOpen(plan)
        ? { ...plan, itemIds: plan.itemIds.filter(id => id !== itemId) }
        : plan,
    ),
  );
}

function belongsToTerminalPlan(plans: ShipmentPlan[], itemId: string) {
  return plans.some(plan => !isOpen(plan) && plan.itemIds.includes(itemId));
}

export function moveItem(
  plans: ShipmentPlan[],
  itemId: string,
  targetPlanId: string,
) {
  const target = plans.find(plan => plan.id === targetPlanId);
  if (!target || !isOpen(target) || belongsToTerminalPlan(plans, itemId)) {
    return plans;
  }

  return withoutEmpty(
    plans.map(plan => {
      if (!isOpen(plan)) {
        return plan;
      }
      if (plan.id === targetPlanId) {
        return plan.itemIds.includes(itemId)
          ? plan
          : { ...plan, itemIds: [...plan.itemIds, itemId] };
      }
      return { ...plan, itemIds: plan.itemIds.filter(id => id !== itemId) };
    }),
  );
}

/** The item leaves for a shipment of its own, which is why it is on the menu. */
export function splitItem(
  plans: ShipmentPlan[],
  itemId: string,
  providerId: string,
) {
  if (belongsToTerminalPlan(plans, itemId)) {
    return plans;
  }

  return [...removeItem(plans, itemId), createPlan(providerId, [itemId])];
}

export function mergeInto(plans: ShipmentPlan[], targetPlanId: string) {
  const target = plans.find(plan => plan.id === targetPlanId);
  if (!target || !isOpen(target)) {
    return plans;
  }

  const gathered = plans.flatMap(plan =>
    isOpen(plan) && plan.id !== targetPlanId ? plan.itemIds : [],
  );

  return plans
    .filter(plan => !isOpen(plan) || plan.id === targetPlanId)
    .map(plan =>
      plan.id === targetPlanId
        ? { ...plan, itemIds: [...plan.itemIds, ...gathered] }
        : plan,
    );
}

export function setPlanProvider(
  plans: ShipmentPlan[],
  planId: string,
  providerId: string,
) {
  return plans.map(plan =>
    plan.id === planId && isOpen(plan) ? { ...plan, providerId } : plan,
  );
}

export function setPlanStatus(
  plans: ShipmentPlan[],
  planId: string,
  status: ShipmentStatus,
  error?: string,
) {
  return plans.map(plan =>
    plan.id === planId ? { ...plan, status, error } : plan,
  );
}

export function findPlanOfItem(plans: ShipmentPlan[], itemId: string) {
  return plans.find(plan => plan.itemIds.includes(itemId));
}

/** The next shipment still waiting to be bought, which is where work resumes. */
export function nextOpenPlan(plans: ShipmentPlan[], afterPlanId?: string) {
  const open = plans.filter(isOpen);
  if (!afterPlanId) {
    return open[0];
  }

  const index = plans.findIndex(plan => plan.id === afterPlanId);
  return (
    plans.slice(index + 1).find(isOpen) ||
    open.find(plan => plan.id !== afterPlanId)
  );
}

/** `Shipment` on its own while there is only one; numbered once there are more. */
export function shipmentTitle(index: number, total: number) {
  return total > 1 ? `Shipment ${index + 1} of ${total}` : 'Shipment';
}

/**
 * The catalog item an order line links to. A V2 line names it in
 * `catalog_item_id` (null while the line is unmapped: no link); a legacy
 * line's `_id` is the item itself.
 */
export const catalogItemId = (item?: {
  _id?: string;
  catalog_item_id?: string | null;
}): string | undefined =>
  item?.catalog_item_id === undefined
    ? item?._id
    : (item.catalog_item_id ?? undefined);

type V2Provider = { _id?: string; name?: string; integration_type?: string };
type V2ProviderLine = {
  content: {
    fulfillment_provider_id?: string | null;
    fulfillment_details?: { _id: string; name: string }[];
  };
};

/**
 * EN-4544: a V2 detail names its providers on the order
 * (`fulfillment_providers`) and on each line only the one it went to
 * (`fulfillment_provider_id`); the legacy per-line `fulfillment_details` the
 * shipment cards read stay empty. This fills them, so a shipment names who
 * shipped it instead of "Unknown provider".
 */
export function withV2LineProviders<
  T extends {
    fulfillment_providers?: V2Provider[];
    order_contents?: V2ProviderLine[];
  },
>(order: T): T {
  const providers = order.fulfillment_providers || [];
  if (!providers.length || !order.order_contents) return order;
  return {
    ...order,
    order_contents: order.order_contents.map(line => {
      const own = providers.filter(
        provider =>
          provider._id && provider._id === line.content.fulfillment_provider_id,
      );
      return line.content.fulfillment_details?.length || !own.length
        ? line
        : {
            ...line,
            content: {
              ...line.content,
              fulfillment_details: own.map(provider => ({
                ...provider,
                _id: provider._id as string,
                name: provider.name || '',
              })),
            },
          };
    }),
  };
}
